import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, WhatsappLogo, EnvelopeSimple, Note, PaperPlaneTilt,
  CheckCircle, Warning, ClockClockwise, FileText, UploadSimple, Paperclip, Copy,
  Bell, Calendar, Trash, Check,
} from "@phosphor-icons/react";
import AddLeadModal from "@/components/AddLeadModal";
import { LEAD_STAGE_LABELS } from "./Templates";
import { DocumentAttachmentList } from "@/components/DocumentAttachment";
import { formatActivityError } from "@/lib/formatActivityError";

const STATUS_LABELS = { "new": "New", contacted: "Contacted", initial: "Initial", fu1: "Follow-up 1", fu2: "Follow-up 2", fu3: "Follow-up 3", fu4: "Follow-up 4", fu5: "Follow-up 5", converted: "Converted", lost: "Lost", quotation_sent: "Quote Sent", qfu1: "Quote FU-1", qfu2: "Quote FU-2", qfu3: "Quote FU-3", qfu4: "Quote FU-4", qfu5: "Quote FU-5" };
const STATUS_COLORS = { "new": "#3B82F6", contacted: "#6B7280", initial: "#6366F1", fu1: "#F59E0B", fu2: "#F97316", fu3: "#EF4444", fu4: "#DC2626", fu5: "#991B1B", converted: "#10B981", lost: "#9CA3AF", quotation_sent: "#8B5CF6", qfu1: "#A78BFA", qfu2: "#8B5CF6", qfu3: "#7C3AED", qfu4: "#6D28D9", qfu5: "#4C1D95" };

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("whatsapp");
  const [msg, setMsg] = useState("");
  const [subject, setSubject] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [onBehalfOf, setOnBehalfOf] = useState("");
  const [whatsAppConnected, setWhatsAppConnected] = useState(false);
  const [emailFollowupEnabled, setEmailFollowupEnabled] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [quoteChannel, setQuoteChannel] = useState("whatsapp");
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get(`/leads/${id}`), api.get(`/leads/${id}/activities`)]);
    setLead(a.data); setActivities(b.data);
  }, [id]);

  useEffect(() => {
    load();
    if (user?.role !== "salesperson") api.get("/users/salespeople?department=lead").then(r => setSalespeople(r.data)).catch(() => { });
    api.get("/templates").then(r => setTemplates(r.data)).catch(() => { });
    api.get("/products").then(r => setProducts(r.data || [])).catch(() => { });
  }, [id, load, user]);

  useEffect(() => {
    if (lead) {
      const spId = lead.assigned_to || user.id;
      api.get(`/whatsapp/status?userId=${spId}`)
        .then(res => setWhatsAppConnected(res.data.status === "CONNECTED"))
        .catch(() => setWhatsAppConnected(false));
    }
  }, [lead, user.id]);

  const [autoLostDays, setAutoLostDays] = useState(30);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showLostReminderModal, setShowLostReminderModal] = useState(false);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderNote, setReminderNote] = useState("");

  useEffect(() => {
    if (lead?.lost_reminder_date) {
      setReminderDate(lead.lost_reminder_date);
      setReminderNote(lead.lost_reminder_note || "");
    }
  }, [lead?.lost_reminder_date, lead?.lost_reminder_note]);

  useEffect(() => {
    api.get("/settings")
      .then(res => {
        setEmailFollowupEnabled(res.data.email_followup_enabled || false);
        if (res.data.auto_lost_days != null) setAutoLostDays(res.data.auto_lost_days);
      })
      .catch(() => setEmailFollowupEnabled(false));
  }, []);

  const retryFollowup = async (stage) => {
    setRetrying(stage);
    try {
      await api.post(`/leads/${id}/retry-followup/${stage}`);
      toast.success("Follow-up sent successfully");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  if (!lead) return <div className="text-[#6B7280]">Loading...</div>;

  const assignedSp = salespeople.find(s => s.id === lead.assigned_to);
  const canActOnBehalf = user.role === "admin" || user.role === "manager";

  const handleFileUpload = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    setUploadingPdf(true);
    try {
      if (files.length === 1) {
        const formData = new FormData();
        formData.append("file", files[0]);
        const res = await api.post("/upload", formData);
        if (res.data?.url) {
          setAttachments(prev => [...prev, res.data.url]);
          toast.success(`Attached "${files[0].name}"`);
        }
      } else {
        const formData = new FormData();
        files.forEach(f => formData.append("files", f));
        const res = await api.post("/upload/multiple", formData);
        const newUrls = (res.data?.files || []).map(f => f.url);
        if (newUrls.length > 0) {
          setAttachments(prev => [...prev, ...newUrls]);
          toast.success(`${newUrls.length} files attached successfully`);
        }
      }
    } catch (err) {
      toast.error("File upload failed");
    } finally {
      setUploadingPdf(false);
      e.target.value = "";
    }
  };

  const send = async (type) => {
    if (!msg.trim() && type !== "note") return toast.error("Message is empty");
    setSending(true);
    try {
      const payload = {
        lead_id: id,
        type,
        content: msg,
        subject: (type === "email" || (type === "quotation" && quoteChannel === "email")) ? subject : "",
        attachments,
        is_quotation_send: type === "quotation",
        channel: type === "quotation" ? quoteChannel : (type === "whatsapp" || type === "email" ? type : null)
      };
      if (onBehalfOf) payload.on_behalf_of = onBehalfOf;
      await api.post(`/leads/${id}/send`, payload);
      toast.success(
        type === "note" ? "Note added" :
          type === "quotation" ? `Quotation sent via ${quoteChannel.toUpperCase()} & follow-up sequence started!` :
            `${type === "whatsapp" ? "WhatsApp" : "Email"} ${process.env.REACT_APP_BACKEND_URL ? "queued" : "sent"}`
      );
      setMsg(""); setSubject(""); setAttachments([]); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errMsg = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || "Send failed");
      toast.error(errMsg);
    } finally { setSending(false); }
  };

  const updateStatus = async (newStatus) => {
    let payload = { status: newStatus };
    if (newStatus === "converted") {
      const amountStr = window.prompt("Enter the product/deal amount for this conversion:");
      if (amountStr === null) return; // User cancelled
      const amount = parseFloat(amountStr);
      if (!isNaN(amount)) {
        payload.amount = amount;
      }
    }
    try {
      await api.patch(`/leads/${id}`, payload);
      toast.success("Status updated");
      load();
      if (newStatus === "lost") {
        if (!reminderDate) {
          const d = new Date(Date.now() + 30 * 86400000);
          setReminderDate(d.toISOString().split('T')[0]);
        }
        if (!reminderNote) {
          setReminderNote(`Namaste ${lead.name}, checking back from JAL Group to see if you have any active requirements.`);
        }
        setShowLostReminderModal(true);
      }
    } catch (e) { toast.error("Failed"); }
  };

  const saveLostReminder = async () => {
    if (!reminderDate) return toast.error("Please select a reminder date");
    try {
      await api.post(`/leads/${id}/lost-reminder`, {
        reminder_date: reminderDate,
        reminder_note: reminderNote,
        status: "pending"
      });
      toast.success("Lost follow-up reminder scheduled");
      setShowLostReminderModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save reminder");
    }
  };

  const clearLostReminder = async () => {
    try {
      await api.post(`/leads/${id}/lost-reminder`, { clear: true });
      toast.success("Reminder removed");
      setShowLostReminderModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove reminder");
    }
  };

  const markReminderCompleted = async () => {
    try {
      await api.post(`/leads/${id}/lost-reminder`, {
        status: "completed",
        reminder_date: lead.lost_reminder_date,
        reminder_note: lead.lost_reminder_note,
      });
      toast.success("Reminder marked as completed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update reminder");
    }
  };

  const markReminderPending = async () => {
    try {
      await api.post(`/leads/${id}/lost-reminder`, {
        status: "pending",
        reminder_date: lead.lost_reminder_date,
        reminder_note: lead.lost_reminder_note,
      });
      toast.success("Reminder marked as pending");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update reminder");
    }
  };

  const reassign = async (sp_id) => {
    try {
      await api.post(`/leads/${id}/assign`, { salesperson_id: sp_id });
      toast.success("Reassigned");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteLead = async () => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      navigate("/leads", { state: { fromLeadDetail: true } });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete lead");
    }
  };

  const applyTemplate = (t) => {
    setMsg(t.body);
    if (t.subject) setSubject(t.subject);
    if (tab !== "quotation") setTab(t.type);
    else if (t.type === "whatsapp" || t.type === "email") setQuoteChannel(t.type);
    setAttachments(t.attachments || []);
  };

  const channelTemplates = templates.filter(t => {
    if ((t.category || "lead") !== "lead") return false;
    const isChannelMatch = tab === "quotation" ? (t.stage === "quotation" || t.stage?.startsWith("qfu") || t.type === quoteChannel) : t.type === tab;
    if (!isChannelMatch) return false;

    if (!t.product_code || t.product_code === "all" || t.product_code === "") return true;
    const pCodeMatch = lead?.product_code != null && String(t.product_code).toLowerCase() === String(lead.product_code).toLowerCase();
    const pInterestMatch = lead?.product_interest != null && String(t.product_code).toLowerCase() === String(lead.product_interest).toLowerCase();
    return pCodeMatch || pInterestMatch;
  }).sort((a, b) => {
    const aSpecific = a.product_code && a.product_code !== "all" && a.product_code !== "";
    const bSpecific = b.product_code && b.product_code !== "all" && b.product_code !== "";
    if (aSpecific && !bSpecific) return -1;
    if (!aSpecific && bSpecific) return 1;
    return 0;
  });

  const timelineActivities = activities.filter(a => a.type !== "note");
  const noteActivities = activities.filter(a => a.type === "note");

  const waQuoteAct = activities.find(a => (a.type === "quotation" || a.is_quotation_send) && a.channel === "whatsapp");
  const emailQuoteAct = activities.find(a => (a.type === "quotation" || a.is_quotation_send) && a.channel === "email");

  const formatQuoteStatus = (act) => {
    if (!act) return "Not Sent";
    let s = act.status;
    if (s === "sent") return "Sent";
    if (s === "queued") return "Pending";
    if (s === "failed") return "Failed";
    return s;
  };

  let quotationStatusText = "Not Sent";
  if (lead.quotation_sent) {
    if (waQuoteAct || emailQuoteAct) {
      quotationStatusText = `WhatsApp: ${formatQuoteStatus(waQuoteAct)} | Email: ${formatQuoteStatus(emailQuoteAct)}`;
    } else {
      quotationStatusText = "Quotation Sent (Active)";
    }
  }

  return (
    <div data-testid="lead-detail-page">
      <button
        type="button"
        onClick={() => {
          if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
          } else {
            navigate("/leads", { state: { fromLeadDetail: true } });
          }
        }}
        className="text-xs font-bold uppercase tracking-widest text-[#6B7280] hover:text-[#002FA7] inline-flex items-center gap-1 mb-4 cursor-pointer"
        data-testid="back-to-leads-btn"
      >
        <ArrowLeft size={12} weight="bold" /> Back to Leads
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6 pb-6 border-b border-[#E5E7EB]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">{lead.name}</h1>
            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border"
              style={{ color: STATUS_COLORS[lead.status], borderColor: STATUS_COLORS[lead.status] }}>
              {STATUS_LABELS[lead.status]}
            </span>
          </div>
          <div className="text-sm text-[#6B7280] break-words">
            {lead.company || "—"} · {lead.product_interest || "—"} · {lead.city || "—"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto items-center">
          <button
            onClick={() => setShowDuplicateModal(true)}
            data-testid="duplicate-lead-btn"
            className="border border-[#002FA7] text-[#002FA7] px-3 py-2 text-sm font-bold hover:bg-blue-50 inline-flex items-center gap-1.5 transition-colors whitespace-nowrap"
            title="Duplicate this lead"
          >
            <Copy size={16} weight="bold" /> Duplicate
          </button>
          {user?.role === "admin" && (
            <button onClick={deleteLead} data-testid="delete-lead-btn"
              className="border border-[#EF4444] text-[#EF4444] px-3 py-2 text-sm font-bold hover:bg-[#FEF2F2]">
              Delete
            </button>
          )}
          <select value={lead.status} onChange={(e) => updateStatus(e.target.value)} data-testid="status-select"
            className="border border-[#D1D5DB] px-3 py-2 text-sm bg-white font-bold w-full sm:w-auto">
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Lead info + composer */}
        <div className="lg:col-span-3 space-y-6">
          {/* Lead info */}
          <section className="border border-[#E5E7EB] p-5">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280] mb-3">Contact</div>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <InfoRow label="Phone" value={lead.phone} />
              <InfoRow label="Email" value={lead.email || "—"} />
              <InfoRow label="Company" value={lead.company || "—"} />
              <InfoRow
                label="Product"
                value={lead.product_interest || "—"}
              />
              <InfoRow label="City" value={lead.city || "—"} />
              <InfoRow label="Source" value={lead.source ? lead.source.replace("_", " ") : "—"} />
              <InfoRow
                label="Creation Type"
                value={
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold rounded ${
                    (lead.entry_type === "Fetched" || (!lead.entry_type && (["meta_ads", "meta"].includes(lead.source) || lead.channel === "meta" || (lead.external_id && lead.external_id.startsWith("meta:")) || (lead.notes && lead.notes.toLowerCase().includes("meta form:")))))
                      ? "bg-purple-50 text-purple-700 border border-purple-200"
                      : "bg-blue-50 text-blue-700 border border-blue-200"
                  }`}>
                    {(lead.entry_type === "Fetched" || (!lead.entry_type && (["meta_ads", "meta"].includes(lead.source) || lead.channel === "meta" || (lead.external_id && lead.external_id.startsWith("meta:")) || (lead.notes && lead.notes.toLowerCase().includes("meta form:"))))) ? "Fetched" : "Manual"}
                  </span>
                }
              />
              <InfoRow label="Amount" value={lead.amount != null ? `₹${lead.amount.toLocaleString()}` : "—"} />
              <InfoRow label="Quotation Status" value={quotationStatusText} />
              {user?.role === "admin" && <InfoRow label="Assigned to" value={assignedSp?.name || (lead.assigned_to ? "—" : "Unassigned")} />}
              <InfoRow label="Created" value={new Date(lead.created_at).toLocaleString('en-IN')} />
              <InfoRow label="Last contact" value={lead.last_contact_at ? new Date(lead.last_contact_at).toLocaleString('en-IN') : "—"} />
              {lead.status === "converted" && (
                <InfoRow
                  label="Converted Date"
                  value={new Date(lead.converted_at || activities.find(a => (a.content || "").toLowerCase().includes("converted"))?.timestamp || lead.last_contact_at || lead.created_at).toLocaleString('en-IN')}
                />
              )}
              {lead.status === "lost" && (
                <InfoRow
                  label="Marked as Lost"
                  value={new Date(lead.lost_at || activities.find(a => (a.content || "").toLowerCase().includes("lost"))?.timestamp || lead.last_contact_at || lead.created_at).toLocaleString('en-IN')}
                />
              )}
              {lead.notes && (
                <div className="col-span-2 pt-3 mt-1 border-t border-[#F3F4F6]">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280]">Notes / Remarks (From Creation)</div>
                  <div className="text-sm font-sans mt-1.5 p-3 bg-[#F9FAFB] border border-[#E5E7EB] text-[#374151] whitespace-pre-wrap font-medium leading-relaxed">
                    {lead.notes}
                  </div>
                </div>
              )}
            </dl>

            {/* Product Quick-Update */}
            {products.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#F3F4F6]">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280] mb-1">Update Product</div>
                <select
                  value={lead.product_code != null ? String(lead.product_code) : ""}
                  onChange={async (e) => {
                    const code = e.target.value;
                    const matched = products.find((p) => String(p.productCode) === String(code));
                    const numCode = code ? parseInt(code, 10) : null;
                    try {
                      await api.patch(`/leads/${id}`, {
                        product_code: isNaN(numCode) ? code : numCode,
                        product_interest: matched ? matched.productName : (lead.product_interest || "")
                      });
                      toast.success("Product updated");
                      load();
                    } catch (err) {
                      toast.error("Failed to update product");
                    }
                  }}
                  className="border border-[#D1D5DB] px-3 py-2 text-sm bg-white w-full font-medium"
                >
                  <option value="">-- Select Product Master --</option>
                  {products.map((p) => (
                    <option key={p.id || p.productCode} value={p.productCode}>
                      {p.productName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {canActOnBehalf && salespeople.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#F3F4F6]">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280] mb-1">Reassign</div>
                <select value={lead.assigned_to || ""} onChange={(e) => reassign(e.target.value)} data-testid="reassign-select"
                  className="border border-[#D1D5DB] px-3 py-2 text-sm bg-white w-full">
                  <option value="" disabled>Pick salesperson</option>
                  {salespeople.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
          </section>

          {/* Lost Lead Re-engagement Reminder Card */}
          {lead.status === "lost" && (
            <section className="border-2 border-[#F59E0B] bg-[#FFFBEB] p-5 shadow-xs" data-testid="lost-lead-reminder-card">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Bell size={18} weight="bold" className="text-[#D97706]" />
                  <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-[#92400E]">
                    Lost Lead Follow-up Reminder
                  </h3>
                </div>
                {lead.lost_reminder_date && (
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 border ${lead.lost_reminder_status === "completed"
                    ? "border-[#10B981] bg-green-50 text-[#059669]"
                    : "border-[#D97706] bg-amber-50 text-[#B45309]"
                    }`}>
                    {lead.lost_reminder_status === "completed" ? "✓ Reminder Completed" : "⏳ Pending Reminder"}
                  </span>
                )}
              </div>

              {lead.lost_reminder_date ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-[#92400E] font-bold">Reminder Date: </span>
                      <span className="font-mono font-bold text-[#111827]">
                        {new Date(lead.lost_reminder_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    {lead.lost_reminder_created_at && (
                      <div className="text-[#78350F]">
                        Set on: {new Date(lead.lost_reminder_created_at).toLocaleDateString('en-IN')}
                      </div>
                    )}
                    {(() => {
                      const lostDate = lead.lost_at || activities.find(a => (a.content || "").toLowerCase().includes("lost"))?.timestamp;
                      if (!lostDate) return null;
                      return (
                        <div className="text-[#78350F]">
                          Marked Lost: {new Date(lostDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      );
                    })()}
                  </div>

                  {lead.lost_reminder_note && (
                    <div className="p-3 bg-white border border-[#FCD34D] text-xs text-[#374151]">
                      <div className="font-bold text-[10px] uppercase tracking-wider text-[#92400E] mb-1">Follow-up Note / Draft Message:</div>
                      <div className="whitespace-pre-wrap">{lead.lost_reminder_note}</div>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#FDE68A]">
                    <button
                      type="button"
                      onClick={() => {
                        setReminderDate(lead.lost_reminder_date || "");
                        setReminderNote(lead.lost_reminder_note || "");
                        setShowLostReminderModal(true);
                      }}
                      className="border border-[#B45309] text-[#B45309] px-3 py-1.5 text-xs font-bold hover:bg-[#FEF3C7] transition-colors"
                    >
                      Edit Reminder
                    </button>
                    {lead.lost_reminder_status !== "completed" ? (
                      <button
                        type="button"
                        onClick={markReminderCompleted}
                        className="bg-[#10B981] text-white px-3 py-1.5 text-xs font-bold hover:bg-[#059669] flex items-center gap-1.5 transition-colors"
                      >
                        <Check size={14} weight="bold" /> Mark Done
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={markReminderPending}
                        className="border border-[#10B981] bg-green-50 text-[#059669] px-3 py-1.5 text-xs font-bold hover:bg-green-100 transition-colors"
                      >
                        ✓ Done (Click to Undo)
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={clearLostReminder}
                      className="text-[#EF4444] border border-[#EF4444] px-3 py-1.5 text-xs font-bold hover:bg-red-50 ml-auto flex items-center gap-1 transition-colors"
                    >
                      <Trash size={14} weight="bold" /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-[#92400E] mb-3">
                    This lead is marked as <b>Lost</b>. Set a reminder date so you don't forget to follow up or send a message to this client later.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (!reminderDate) {
                        const d = new Date(Date.now() + 30 * 86400000);
                        setReminderDate(d.toISOString().split('T')[0]);
                      }
                      if (!reminderNote) {
                        setReminderNote(`Namaste ${lead.name}, checking back from JAL Group to see if you have any active requirements.`);
                      }
                      setShowLostReminderModal(true);
                    }}
                    className="bg-[#D97706] text-white px-4 py-2 text-xs font-bold hover:bg-[#B45309] flex items-center gap-1.5 transition-colors"
                  >
                    <Bell size={15} weight="bold" /> Set Re-engagement Reminder
                  </button>
                </div>
              )}
            </section>
          )}

          {/* Follow-up schedule */}
          <section className="border border-[#E5E7EB] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ClockClockwise size={16} weight="bold" />
                <div className="text-[10px] font-bold uppercase tracking-widest">
                  {lead.quotation_sent ? "Quotation Follow-up Schedule (Active)" : "General Follow-up Schedule"}
                </div>
              </div>
              {lead.quotation_sent && (
                <span className="bg-purple-100 text-[#7C3AED] border border-purple-300 text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">
                  Quotation Flow Active
                </span>
              )}
            </div>
            {lead.status === "converted" && (
              <div className="mb-4 flex flex-col gap-2">
                <div className="text-xs text-emerald-800 bg-emerald-50 p-2.5 border border-emerald-200 font-medium flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                    <span>🎉 Lead Converted on <strong>{lead.converted_at ? new Date(lead.converted_at).toLocaleDateString('en-IN') + ' ' + new Date(lead.converted_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : (lead.last_contact_at ? new Date(lead.last_contact_at).toLocaleDateString('en-IN') : 'recently')}</strong></span>
                  </div>
                  {lead.amount != null && (
                    <span className="font-bold text-emerald-900 bg-emerald-100 px-2 py-0.5 border border-emerald-300 rounded">
                      Deal Amount: ₹{lead.amount.toLocaleString('en-IN')}
                    </span>
                  )}
                </div>
              </div>
            )}
            {lead.quotation_sent && (
              <div className="mb-4 flex flex-col gap-2">
                <div className="text-xs text-[#6B7280] bg-purple-50 p-2 border border-purple-200">
                  ✨ Quotation sent on {lead.quotation_sent_at ? new Date(lead.quotation_sent_at).toLocaleDateString('en-IN') : 'recently'}.
                  Old general follow-ups have been automatically cancelled and replaced by this quotation follow-up cadence.
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-sm font-bold text-[#002FA7] bg-[#F3F4F6] p-2 border border-[#E5E7EB] inline-flex items-center gap-1.5 shadow-sm">
                    <WhatsappLogo size={16} weight="fill" className="text-[#25D366]" />
                    Quotation sent via WhatsApp: {formatQuoteStatus(waQuoteAct)}
                  </div>
                  <div className="text-sm font-bold text-[#002FA7] bg-[#F3F4F6] p-2 border border-[#E5E7EB] inline-flex items-center gap-1.5 shadow-sm">
                    <EnvelopeSimple size={16} weight="fill" className="text-[#002FA7]" />
                    Quotation sent via Email: {formatQuoteStatus(emailQuoteAct)}
                  </div>
                </div>
              </div>
            )}
            <div className={`grid gap-3 ${lead.quotation_sent ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6" : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7"}`}>
              {(lead.quotation_sent ? ["qfu1", "qfu2", "qfu3", "qfu4", "qfu5"] : ["initial", "fu1", "fu2", "fu3", "fu4", "fu5"]).map((s) => {
                const due = lead[`${s}_due_at`];
                const sent = lead[`${s}_sent_at`];
                const status = lead[`${s}_status`];
                const isFailed = status === "failed";
                const isQueued = status === "queued";
                const isCancelled = status === "cancelled";
                const isPendingOrFailed = isFailed || isQueued;

                return (
                  <div key={s} className={`border p-3 flex flex-col justify-between ${lead.quotation_sent ? "border-purple-200 bg-purple-50/20" : "border-[#E5E7EB]"}`} data-testid={`schedule-${s}`}>
                    <div>
                      <div className={`text-[10px] font-bold uppercase tracking-widest ${lead.quotation_sent ? "text-[#7C3AED]" : "text-[#6B7280]"}`}>
                        {s === "initial" ? "Initial Msg" : lead.quotation_sent ? `Quote FU-${s.replace("qfu", "")}` : `Follow-up ${s.replace("fu", "")}`}
                      </div>
                      <div className="text-xs font-mono mt-1">
                        {isFailed ? (
                          <span className="text-[#EF4444] flex items-center gap-1">
                            <Warning size={12} weight="bold" /> Failed
                          </span>
                        ) : isQueued ? (
                          <span className="text-[#F59E0B] flex items-center gap-1">
                            <ClockClockwise size={12} weight="bold" /> Queued
                          </span>
                        ) : isCancelled ? (
                          <span className="text-[#9CA3AF] flex items-center gap-1">
                            ✕ Stopped
                          </span>
                        ) : sent ? (
                          <span className="text-[#10B981] flex flex-col gap-0.5">
                            <span className="flex items-center gap-1 font-semibold"><CheckCircle size={12} weight="fill" /> Sent</span>
                            <span className="text-[11px] text-[#374151] font-normal">{new Date(sent).toLocaleDateString()} {new Date(sent).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </span>
                        ) : due ? (
                          <span className="flex flex-col gap-0.5">
                            <span className="text-[#6B7280] font-semibold">Due</span>
                            <span className="text-[11px] text-[#374151] font-normal">{new Date(due).toLocaleDateString()} {new Date(due).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </span>
                        ) : (
                          "—"
                        )}
                      </div>
                    </div>
                    {isPendingOrFailed && (
                      <div className="mt-2 pt-2 border-t border-[#F3F4F6]">
                        <button
                          onClick={() => retryFollowup(s)}
                          disabled={retrying === s}
                          className="bg-[#002FA7] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 hover:bg-[#002280] disabled:opacity-60 transition-colors w-full text-center"
                        >
                          {retrying === s ? (isQueued ? "Sending..." : "Retrying...") : (isQueued ? "Send Now" : "Retry")}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              <div className={`border p-3 flex flex-col justify-between ${lead.status === "converted" ? "border-emerald-400 bg-emerald-50/40" : lead.status === "lost" ? "border-gray-400 bg-gray-50" : lead.quotation_sent ? "border-purple-200 bg-purple-50/20" : "border-[#E5E7EB]"}`} data-testid="schedule-lost">
                <div>
                  <div className={`text-[10px] font-bold uppercase tracking-widest ${lead.status === "converted" ? "text-emerald-700" : lead.status === "lost" ? "text-gray-700" : lead.quotation_sent ? "text-[#7C3AED]" : "text-[#6B7280]"}`}>
                    {lead.status === "converted" ? "Converted" : "Lost"}
                  </div>
                  <div className="text-xs font-mono mt-1">
                    {lead.status === "converted" ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-emerald-700 flex items-center gap-1 font-semibold">
                          <CheckCircle size={12} weight="fill" /> Converted
                        </span>
                        {(() => {
                          const convDate = lead.converted_at || activities.find(a => (a.content || "").toLowerCase().includes("converted"))?.timestamp || lead.last_contact_at;
                          if (!convDate) return null;
                          const d = new Date(convDate);
                          return (
                            <span className="text-[11px] text-[#374151] font-normal" data-testid="converted-date-schedule">
                              {d.toLocaleDateString('en-IN')} {d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          );
                        })()}
                      </div>
                    ) : lead.status === "lost" ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-gray-700 flex items-center gap-1 font-semibold">
                          <CheckCircle size={12} weight="fill" /> Marked Lost
                        </span>
                        {(() => {
                          const lostDate = lead.lost_at || activities.find(a => (a.content || "").toLowerCase().includes("lost"))?.timestamp;
                          if (!lostDate) return null;
                          const d = new Date(lostDate);
                          return (
                            <span className="text-[11px] text-[#374151] font-normal" data-testid="marked-lost-date">
                              {d.toLocaleDateString('en-IN')} {d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          );
                        })()}
                      </div>
                    ) : (
                      (() => {
                        const fu5Due = lead.quotation_sent ? lead.qfu5_due_at : lead.fu5_due_at;
                        if (!fu5Due) return "—";
                        const lostDue = new Date(new Date(fu5Due).getTime() + (autoLostDays * 86400000));
                        return (
                          <span className="flex flex-col gap-0.5">
                            <span className="text-[#6B7280] font-semibold">Auto-Lost Due</span>
                            <span className="text-[11px] text-[#374151] font-normal">{lostDue.toLocaleDateString('en-IN')} {lostDue.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                          </span>
                        );
                      })()
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Composer */}
          <section className="border border-[#E5E7EB] p-5">
            <div className="flex border-b border-[#E5E7EB] mb-4 -mx-5 px-5 overflow-x-auto">
              {[
                { k: "whatsapp", l: "WhatsApp", Icon: WhatsappLogo },
                { k: "email", l: "Email", Icon: EnvelopeSimple },
                { k: "quotation", l: "Send Quotation", Icon: FileText },
                { k: "note", l: "Note", Icon: Note },
              ].map(({ k, l, Icon }) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`compose-tab-${k}`}
                  className={`px-4 py-3 text-sm font-bold flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? (k === "quotation" ? "border-[#7C3AED] text-[#7C3AED]" : "border-[#002FA7] text-[#002FA7]") : "border-transparent text-[#6B7280] hover:text-[#111827]"
                    }`}>
                  <Icon size={16} weight="bold" /> {l}
                </button>
              ))}
            </div>

            {tab === "quotation" && (
              <div className="mb-4 p-3.5 bg-purple-50 border border-purple-200">
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#6D28D9] mb-2.5">Quotation Delivery Channel</div>
                <div className="flex gap-6 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-[#111827]">
                    <input type="radio" name="quoteChannel" checked={quoteChannel === "whatsapp"} onChange={() => setQuoteChannel("whatsapp")} className="accent-[#7C3AED]" />
                    <WhatsappLogo size={18} weight="fill" className="text-green-600" /> Send via WhatsApp
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-[#111827]">
                    <input type="radio" name="quoteChannel" checked={quoteChannel === "email"} onChange={() => setQuoteChannel("email")} className="accent-[#7C3AED]" />
                    <EnvelopeSimple size={18} weight="fill" className="text-blue-600" /> Send via Email
                  </label>
                </div>
                <div className="text-[11px] text-[#6B7280]">
                  ⚡ Sending a quotation updates status to <b>Quotation Sent</b> and activates the automated quotation follow-up cadence while stopping general follow-ups.
                </div>
              </div>
            )}

            {canActOnBehalf && tab !== "note" && (
              <div className="mb-3">
                <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280]">Send on behalf of (manager)</label>
                <select value={onBehalfOf} onChange={(e) => setOnBehalfOf(e.target.value)} data-testid="on-behalf-select"
                  className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white mt-1">
                  <option value="">Default ({assignedSp?.name || "auto"})</option>
                  {salespeople.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}

            {(tab === "email" || (tab === "quotation" && quoteChannel === "email")) && (
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (e.g. Quotation for product...)"
                data-testid="compose-subject"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm mb-2 focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            )}
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={5}
              data-testid="compose-body"
              placeholder={tab === "note" ? "Add an internal team note..." : tab === "quotation" ? `Type your quotation message. Select a template below or attach a manual PDF quotation.` : `Type your ${tab} message. You can use {name}, {product}, {company}, {salesperson}.`}
              className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />

            {tab !== "note" && (
              <div className="mt-2 flex items-center justify-between bg-gray-50 p-2.5 border border-[#E5E7EB]">
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer bg-white border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold text-[#374151] hover:bg-gray-100 flex items-center gap-1.5 shadow-sm transition-all hover:border-[#002FA7]">
                    <UploadSimple size={14} weight="bold" className="text-[#002FA7]" />
                    {uploadingPdf ? "Uploading..." : "Attach Documents / Media"}
                    <input
                      type="file"
                      multiple
                      accept=".pdf,application/pdf,image/*,video/*,.mp4,.mov,.webm,.doc,.docx,.xls,.xlsx,.csv,.txt"
                      onChange={handleFileUpload}
                      disabled={uploadingPdf}
                      className="hidden"
                    />
                  </label>
                  <span className="text-[11px] text-[#6B7280]">
                    {tab === "quotation" ? "Attach quotation PDF, brochures, images or demo videos" : "Attach documents, images, or demo videos"}
                  </span>
                </div>
                {attachments.length > 0 && <span className="text-xs font-bold text-[#10B981]">{attachments.length} attached</span>}
              </div>
            )}

            {attachments.length > 0 && tab !== "note" && (
              <div className="mt-2">
                <DocumentAttachmentList
                  attachments={attachments}
                  onRemove={(idx) => setAttachments(attachments.filter((_, i) => i !== idx))}
                />
              </div>
            )}

            {tab !== "note" && (
              <div className="mt-3 mb-2">
                <select
                  onChange={(e) => {
                    const t = channelTemplates.find(x => x.id === e.target.value);
                    if (t) applyTemplate(t);
                    e.target.value = "";
                  }}
                  disabled={channelTemplates.length === 0}
                  className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none disabled:bg-[#F3F4F6] disabled:text-[#6B7280]"
                >
                  <option value="">
                    {channelTemplates.length > 0
                      ? `Insert a ${tab === "quotation" ? quoteChannel : tab} template...`
                      : `No ${tab} templates available`}
                  </option>
                  {channelTemplates.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name} (Lead · {LEAD_STAGE_LABELS[t.stage] || t.stage})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex justify-end mt-4">
              <button onClick={() => send(tab)} disabled={sending || uploadingPdf} data-testid="compose-send-button"
                className={`${tab === "quotation" ? "bg-[#7C3AED] hover:bg-[#6D28D9]" : "bg-[#002FA7] hover:bg-[#002280]"} text-white font-bold px-5 py-2.5 flex items-center gap-2 disabled:opacity-60 transition-colors`}>
                <PaperPlaneTilt size={16} weight="bold" />
                {sending ? "Sending..." : tab === "note" ? "Save Note" : tab === "quotation" ? `Send Quotation via ${quoteChannel === "whatsapp" ? "WhatsApp" : "Email"}` : `Send ${tab === "whatsapp" ? "WhatsApp" : "Email"}`}
              </button>
            </div>
          </section>
        </div>

        {/* Right: Notes & Timeline */}
        <aside className="lg:col-span-2 space-y-6">
          {/* Dedicated Notes Section */}
          <div className="border border-[#E5E7EB] p-5 bg-amber-50/40 shadow-xs">
            <div className="flex items-center gap-2 mb-3.5">
              <Note size={18} weight="bold" className="text-amber-600" />
              <h3 className="font-heading font-bold text-lg text-[#111827]">
                Internal Team Notes ({noteActivities.length + (lead.notes ? 1 : 0)})
              </h3>
            </div>
            {noteActivities.length === 0 && !lead.notes ? (
              <div className="text-sm text-[#6B7280] py-5 text-center border border-dashed border-[#D1D5DB] bg-white">
                No notes recorded. Use the "Note" tab below to add internal team notes for this lead.
              </div>
            ) : (
              <div className="space-y-3 max-h-[350px] overflow-y-auto pr-1">
                {lead.notes && (
                  <div className="p-3.5 bg-white border-l-4 border-amber-500 border-y border-r border-[#E5E7EB] text-sm">
                    <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-amber-800 mb-1.5">
                      <span>Initial Note (From Import / Creation)</span>
                      <span>{new Date(lead.created_at).toLocaleDateString('en-IN')}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-[#374151] font-sans">{lead.notes}</p>
                  </div>
                )}
                {noteActivities.map(n => {
                  const sp = salespeople.find(s => s.id === n.user_id);
                  return (
                    <div key={n.id} className="p-3.5 bg-white border border-[#E5E7EB] text-sm shadow-2xs">
                      <p className="whitespace-pre-wrap text-[#374151] mb-2 font-sans">{n.content}</p>
                      <div className="flex items-center justify-between text-[10px] text-[#6B7280] font-mono border-t border-gray-100 pt-2">
                        <span>{new Date(n.timestamp).toLocaleString('en-IN')}</span>
                        {sp && <span className="font-sans font-semibold text-[#111827]">by {sp.name}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="border border-[#E5E7EB] p-5 lg:sticky lg:top-4 bg-white">
            <h3 className="font-heading font-bold text-xl mb-4">Activity Timeline</h3>
            {timelineActivities.length === 0 ? (
              <div className="text-sm text-[#6B7280] py-6 text-center">No communication activity yet. Send your first message or quotation.</div>
            ) : (
              <div>
                <div className="lp-timeline pl-8 space-y-4">
                  {(showAllActivities ? timelineActivities : timelineActivities.slice(0, 5)).map(a => (
                    <TimelineItem
                      key={a.id}
                      a={a}
                      salespeople={salespeople}
                      onRetry={retryFollowup}
                      whatsAppConnected={whatsAppConnected}
                      emailFollowupEnabled={emailFollowupEnabled}
                      retrying={retrying}
                    />
                  ))}
                </div>
                {timelineActivities.length > 5 && (
                  <div className="mt-6 pt-4 border-t border-[#E5E7EB] flex justify-center">
                    <button
                      onClick={() => setShowAllActivities(!showAllActivities)}
                      className="text-xs font-bold uppercase tracking-wider text-[#002FA7] hover:bg-[#F3F4F6] px-4 py-2 border border-[#E5E7EB] transition-colors"
                    >
                      {showAllActivities ? "Show Less Activities" : `View All Activities (${timelineActivities.length})`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>

      {showDuplicateModal && (
        <AddLeadModal
          onClose={() => setShowDuplicateModal(false)}
          salespeople={salespeople}
          role={user.role}
          initialData={lead}
          onCreated={(newLead) => {
            setShowDuplicateModal(false);
            if (newLead?.id) {
              navigate(`/leads/${newLead.id}`);
            }
          }}
        />
      )}

      {/* Lost Lead Reminder Modal */}
      {showLostReminderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white border border-[#111827] shadow-2xl max-w-lg w-full p-6 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-[#E5E7EB] mb-4">
              <div className="flex items-center gap-2">
                <Bell size={20} weight="bold" className="text-[#D97706]" />
                <h3 className="font-heading font-black text-lg text-[#111827]">
                  Lost Lead Follow-up Reminder
                </h3>
              </div>
              <button
                onClick={() => setShowLostReminderModal(false)}
                className="text-[#6B7280] hover:text-[#111827] text-lg font-bold"
              >
                ×
              </button>
            </div>

            <p className="text-xs text-[#6B7280] mb-4">
              Schedule a future reminder date to follow up or send a message to <b>{lead.name}</b>. It will appear on your Dashboard for that month.
            </p>

            {/* Quick date presets */}
            <div className="mb-4">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">
                Quick Date Presets
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: "+1 Month", days: 30 },
                  { label: "+2 Months", days: 60 },
                  { label: "+3 Months", days: 90 },
                  { label: "+6 Months", days: 180 },
                ].map(p => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => {
                      const d = new Date(Date.now() + p.days * 86400000);
                      setReminderDate(d.toISOString().split('T')[0]);
                    }}
                    className="px-2 py-1.5 text-xs font-bold border border-[#D1D5DB] hover:bg-[#F3F4F6] text-[#374151]"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">
                Reminder Date *
              </label>
              <input
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
            </div>

            <div className="mb-5">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">
                Message / Follow-up Note to Send
              </label>
              <textarea
                rows={3}
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                placeholder="e.g. Namaste, checking back from JAL Group to see if your requirements have evolved or if you'd like a revised proposal."
                className="w-full border border-[#D1D5DB] p-3 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-[#E5E7EB]">
              <button
                type="button"
                onClick={() => setShowLostReminderModal(false)}
                className="px-4 py-2 text-xs font-bold text-[#6B7280] hover:text-[#111827]"
              >
                Cancel
              </button>
              <div className="flex gap-2">
                {lead.lost_reminder_date && (
                  <button
                    type="button"
                    onClick={clearLostReminder}
                    className="px-3 py-2 text-xs font-bold text-[#EF4444] border border-[#EF4444] hover:bg-red-50"
                  >
                    Remove
                  </button>
                )}
                <button
                  type="button"
                  onClick={saveLostReminder}
                  className="px-5 py-2 text-xs font-bold bg-[#002FA7] text-white hover:bg-[#002280] transition-colors"
                >
                  Save Reminder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280]">{label}</div>
      <div className="text-sm font-mono mt-0.5">{value}</div>
    </div>
  );
}

function TimelineItem({ a, salespeople, onRetry, whatsAppConnected, emailFollowupEnabled, retrying }) {
  const [expanded, setExpanded] = useState(false);
  const sp = salespeople.find(s => s.id === a.user_id);
  const onBehalfSp = salespeople.find(s => s.id === a.on_behalf_of);
  const Icon = a.type === "whatsapp" ? WhatsappLogo : a.type === "email" ? EnvelopeSimple : a.type === "quotation" ? FileText : a.type === "follow_up_auto" ? ClockClockwise : a.type === "lost_reminder" ? Bell : Note;

  const statusBadge = (() => {
    if (a.type === "status_change" || a.type === "note") {
      return null;
    }
    if (a.type === "lost_reminder") {
      if (a.status === "cancelled" || a.status === "removed" || (a.content && a.content.toLowerCase().includes("removed"))) {
        return { c: "#EF4444", t: "Removed" };
      }
      if (a.status === "completed" || (a.content && a.content.toLowerCase().includes("completed"))) {
        return { c: "#10B981", t: "Completed" };
      }
      return { c: "#D97706", t: "Reminder Set" };
    }
    return {
      sent: { c: "#10B981", t: "Sent" },
      simulated: { c: "#3B82F6", t: "Simulated" },
      queued: { c: "#F59E0B", t: "Queued" },
      failed: { c: "#EF4444", t: "Failed" },
      scheduled: { c: "#D97706", t: "Scheduled" },
      completed: { c: "#10B981", t: "Completed" },
      removed: { c: "#EF4444", t: "Removed" },
      cancelled: { c: "#EF4444", t: "Removed" },
    }[a.status] || { c: "#6B7280", t: a.status };
  })();

  const isFollowUpFailed = a.type === "follow_up_auto" && a.status === "failed";
  const isFollowUpQueued = a.type === "follow_up_auto" && a.status === "queued";
  const isFollowUpPendingOrFailed = isFollowUpFailed || isFollowUpQueued;
  const isLongContent = (a.content || "").length > 120 || (a.content || "").split("\n").length > 2;

  return (
    <div className="relative" data-testid={`activity-${a.id}`}>
      <div className="lp-timeline-dot" style={{ background: a.status === "failed" ? "#EF4444" : a.type === "lost_reminder" ? "#D97706" : a.type === "status_change" ? "#10B981" : "#002FA7" }} />
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} weight="bold" className={a.type === "lost_reminder" ? "text-[#D97706]" : a.type === "status_change" ? "text-[#10B981]" : ""} />
        <span className="text-xs font-bold uppercase tracking-widest">
          {a.type.replace("_", " ")}
          {a.follow_up_stage ? ` · ${a.follow_up_stage.toUpperCase()}` : ""}
          {a.type !== "lost_reminder" && a.channel && a.channel !== "other" ? ` · ${a.channel.toUpperCase()}` : ""}
        </span>
        {statusBadge && (
          <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 border"
            style={{ color: statusBadge.c, borderColor: statusBadge.c }}>{statusBadge.t}</span>
        )}
      </div>
      {a.subject && <div className="text-sm font-bold">{a.subject}</div>}
      <div className="mt-1">
        <p className={`text-sm text-[#374151] whitespace-pre-wrap ${!expanded && isLongContent ? 'line-clamp-2 overflow-hidden text-ellipsis' : ''}`}>
          {a.content}
        </p>
        {isLongContent && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-[#002FA7] font-bold hover:underline mt-1 focus:outline-none block"
          >
            {expanded ? "Show Less Details ▲" : "View Full Details ▼"}
          </button>
        )}
      </div>
      {a.attachments && a.attachments.length > 0 && (
        <div className="mt-2">
          <DocumentAttachmentList attachments={a.attachments} showThumbnails={false} />
        </div>
      )}
      <div className="text-[10px] text-[#6B7280] font-mono mt-1">
        {new Date(a.timestamp).toLocaleString('en-IN')}
        {onBehalfSp && sp && <> · {sp.name} acting as <b>{onBehalfSp.name}</b></>}
      </div>
      {a.error && <div className={`text-[10px] ${a.status === "queued" ? "text-[#D97706]" : "text-[#EF4444]"} mt-1 flex items-center gap-1`}><Warning size={10} weight="fill" />{formatActivityError(a.error, sp?.name || onBehalfSp?.name)}</div>}
      {isFollowUpPendingOrFailed && (
        <div className="mt-2">
          <button
            onClick={() => onRetry(a.follow_up_stage)}
            disabled={retrying === a.follow_up_stage}
            className="bg-[#002FA7] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 hover:bg-[#002280] disabled:opacity-60 transition-colors"
          >
            {retrying === a.follow_up_stage ? (isFollowUpQueued ? "Sending..." : "Retrying...") : (isFollowUpQueued ? "Send Now" : "Retry Follow-up")}
          </button>
        </div>
      )}
    </div>
  );
}
